/* eslint-disable react/require-default-props */
import React, { useEffect, useRef, useState } from 'react';

import Loader from 'components/atoms/loader';
import { useProposalShell } from 'components/organisms/ai_management_bidding_proposal/context';
import { useProposalCheck } from 'hooks/useProposalCheck';
import type {
  ProposalCheckItemResponse,
  ProposalCheckOndeCorrigir,
  ProposalCheckResponse,
  ProposalCheckStatus,
} from 'services/actions/ai_management_proposal_check';
import {
  RULE_TYPE_LABEL,
  ProposalStep,
  Rule,
  proposalFieldTarget,
  proposalItemTarget,
  proposalRequirementTarget,
  proposalRuleTarget,
  ruleTitle,
} from 'services/ai_management_proposal';
import { Container } from './styles';

interface AiManagementProposalCheckProps {
  biddingId: number;
  rules: Rule[];
  onOpenEdital?: (rule?: Rule) => void;
  onCheckChange?: (check: ProposalCheckResponse | null) => void;
  // Gravações em andamento na casca: a checagem relê quando terminam, para não ficar com o estado
  // de antes de uma alteração feita ao sair da etapa anterior.
  saving?: number;
}

const STATUS_BADGE: Record<
  ProposalCheckStatus,
  { label: string; className: string; icon: string; card: string }
> = {
  atendida: { label: 'Atendida', className: 'status-met', icon: 'check_circle_outline', card: '' },
  nao_atendida: {
    label: 'Não atendida',
    className: 'status-unmet',
    icon: 'highlight_off',
    card: 'failed',
  },
  requer_atencao: {
    label: 'Requer atenção',
    className: 'status-attention',
    icon: 'error_outline',
    card: 'attention',
  },
  ciente: { label: 'Ciente', className: 'status-aware', icon: 'done', card: '' },
  a_conferir: {
    label: 'A conferir',
    className: 'status-attention',
    icon: 'error_outline',
    card: 'attention',
  },
  conferida: {
    label: 'Conferida',
    className: 'status-met',
    icon: 'check_circle_outline',
    card: '',
  },
};

const BASIC_LABEL: Record<string, string> = {
  preco_itens: 'Valor unitário dos itens',
  empresa_confirmada: 'Dados da empresa confirmados',
  representante_legal: 'Representante legal',
  validade_proposta: 'Validade da proposta',
  declaracoes: 'Declarações e documentos de Antes do lance',
};

const STEP_OF: Record<ProposalCheckOndeCorrigir, ProposalStep> = {
  regras: 'regras',
  itens: 'itens',
  empresa: 'empresa',
  condicoes: 'empresa',
  declaracoes: 'declaracoes',
};

const targetOf = (item: ProposalCheckItemResponse): string | undefined => {
  const alvo = item.campo_alvo;
  if (item.onde_corrigir === 'itens' && alvo && typeof alvo === 'object') {
    const { numero_lote: lote, numero_item: numero } = alvo as {
      numero_lote: number | null;
      numero_item: number | null;
    };
    return proposalItemTarget(lote, numero);
  }
  if (typeof alvo !== 'string' || !alvo) return undefined;
  if (item.onde_corrigir === 'regras') return proposalRuleTarget(alvo);
  if (item.onde_corrigir === 'declaracoes') return proposalRequirementTarget(alvo);
  return proposalFieldTarget(alvo);
};

const CIENTE_CONFIRM = 'Tem certeza? Esta regra deixará de ser considerada na checagem.';

const AiManagementProposalCheck: React.FC<AiManagementProposalCheckProps> = ({
  biddingId,
  rules,
  onOpenEdital,
  onCheckChange,
  saving = 0,
}) => {
  const { trackSave, goToStep } = useProposalShell();
  const {
    check,
    loading,
    loadError,
    busy,
    errors,
    undoToast,
    reload,
    markAware,
    undoAware,
    markChecked,
  } = useProposalCheck(biddingId, { trackSave });
  const [confirming, setConfirming] = useState<string | null>(null);

  useEffect(() => {
    onCheckChange?.(check);
  }, [check, onCheckChange]);

  const savingBefore = useRef(saving);
  useEffect(() => {
    if (savingBefore.current > 0 && saving === 0) reload();
    savingBefore.current = saving;
  }, [saving, reload]);

  if (loading && !check) return <Loader />;
  if (loadError && !check) {
    return (
      <div className="alert alert-warning d-flex align-items-center justify-content-between">
        <span>Não foi possível carregar a checagem da proposta.</span>
        <button type="button" className="btn btn-sm btn-outline-secondary ml-3" onClick={reload}>
          Tentar de novo
        </button>
      </div>
    );
  }
  if (!check) return null;

  const titleOf = (item: ProposalCheckItemResponse) => {
    if (item.origem === 'basica') return BASIC_LABEL[item.regra] ?? item.regra;
    if (item.origem === 'declaracoes') return 'Declarações e documentos';
    const rule = rules.find((r) => r.regra === item.regra);
    return rule ? ruleTitle(rule) : RULE_TYPE_LABEL[item.regra] ?? item.regra;
  };

  const basica = check.itens.some((item) => item.origem === 'basica');
  const {
    atendidas,
    requer_atencao: requerAtencao,
    nao_atendidas: naoAtendidas,
    total,
  } = check.contadores;

  const percentual = total > 0 ? Math.round((atendidas / total) * 100) : 0;

  const controversaSemLeitura = (item: ProposalCheckItemResponse) => {
    const rule = rules.find((r) => r.regra === item.regra);
    return !!rule?.controversa && rule.leitura_escolhida == null;
  };

  const renderCorrigir = (item: ProposalCheckItemResponse) => {
    const onde = item.onde_corrigir;
    if (!onde) return null;
    return (
      <button
        type="button"
        className="btn btn-sm btn-primary"
        onClick={() => goToStep(STEP_OF[onde], targetOf(item))}
      >
        Corrigir
      </button>
    );
  };

  const renderActions = (item: ProposalCheckItemResponse) => {
    const disabled = busy === item.regra;
    if (item.status === 'nao_atendida') {
      return (
        <>
          {renderCorrigir(item)}
          {(item.origem === 'edital' || item.origem === 'cliente') && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => goToStep('regras', proposalRuleTarget(item.regra))}
            >
              Revisar regra
            </button>
          )}
        </>
      );
    }
    if (item.status === 'requer_atencao') {
      if (confirming === item.regra) {
        return (
          <div className="d-flex align-items-center flex-wrap">
            <span className="mr-2">{CIENTE_CONFIRM}</span>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={disabled}
              onClick={() => {
                setConfirming(null);
                markAware(item.regra);
              }}
            >
              Confirmar
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setConfirming(null)}
            >
              Cancelar
            </button>
          </div>
        );
      }
      return (
        <>
          {controversaSemLeitura(item) && renderCorrigir(item)}
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={disabled}
            onClick={() => setConfirming(item.regra)}
          >
            Ciente
          </button>
        </>
      );
    }
    if (item.status === 'a_conferir') {
      return (
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          disabled={disabled}
          onClick={() => markChecked(item.regra)}
        >
          Marcar como conferida
        </button>
      );
    }
    return null;
  };

  return (
    <Container>
      <section className="progress-card">
        <div className="progress-head">
          <strong>
            {basica ? 'Checagem básica' : `${atendidas} de ${total} regras atendidas`}
          </strong>
          <span>{percentual}%</span>
        </div>
        <div className="progress-bar-track">
          <span style={{ width: `${percentual}%` }} />
        </div>
        <div className="progress-summary">
          <span className="text-success">
            <i className="material-icons-outlined" aria-hidden="true">
              check_circle_outline
            </i>
            {atendidas} atendidas
          </span>
          <span className="text-attention">
            <i className="material-icons-outlined" aria-hidden="true">
              error_outline
            </i>
            {requerAtencao} requerem atenção
          </span>
          <span className="text-danger">
            <i className="material-icons-outlined" aria-hidden="true">
              highlight_off
            </i>
            {naoAtendidas} não atendidas
          </span>
        </div>
      </section>

      <div className="checks">
        {check.itens.map((item) => {
          const badge = STATUS_BADGE[item.status];
          const key = `${item.origem}-${item.regra}-${String(item.campo_alvo ?? '')}`;
          const acoes = renderActions(item);
          return (
            <article key={key} className={`check-card ${badge.card}`}>
              <span className={`rule-status-badge ${badge.className}`}>
                <i className="material-icons-outlined" aria-hidden="true">
                  {badge.icon}
                </i>
                {badge.label}
              </span>
              <h3>{titleOf(item)}</h3>
              {item.detalhe && <p className="rule-detail">{item.detalhe}</p>}
              {item.trecho && (
                <blockquote>
                  “{item.trecho}”
                  {onOpenEdital && (
                    <small>
                      <button
                        type="button"
                        className="btn btn-link p-0"
                        onClick={() => onOpenEdital(rules.find((r) => r.regra === item.regra))}
                      >
                        Abrir edital
                        <i className="material-icons-outlined" aria-hidden="true">
                          open_in_new
                        </i>
                      </button>
                    </small>
                  )}
                </blockquote>
              )}
              {acoes && <div className="rule-actions">{acoes}</div>}
              {errors[item.regra] && (
                <div className="text-danger small mt-1">{errors[item.regra]}</div>
              )}
            </article>
          );
        })}
      </div>

      {undoToast && (
        <div className="alert alert-dark d-flex align-items-center justify-content-between py-2 mt-3">
          <span>{undoToast.message}</span>
          <button type="button" className="btn btn-sm btn-link p-0" onClick={undoAware}>
            Desfazer
          </button>
        </div>
      )}
    </Container>
  );
};

export default AiManagementProposalCheck;
